import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, LabRequest } from '../../types';
import { FlaskConical, CheckCircle, Printer, XCircle, CalendarClock, History } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal } from '../../store/labStructures';
export const LaboratoryDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    logAudit
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    lab: LabRequest;
  } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [labComments, setLabComments] = useState(''); // Added state for lab comments
  // Modals state
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    reason: ''
  });
  const [rescheduleModal, setRescheduleModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    newDate: string;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    newDate: '',
    reason: ''
  });
  const [showHistory, setShowHistory] = useState(false);
  // Find encounters with pending lab requests
  const pendingRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Pending').map(lab => ({
    enc,
    lab
  })));
  const rescheduledRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  })));
  const historyRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Cancelled' || lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  }))).sort((a, b) => {
    const timeA = new Date(a.lab.cancelledAt || a.lab.rescheduledAt || 0).getTime();
    const timeB = new Date(b.lab.cancelledAt || b.lab.rescheduledAt || 0).getTime();
    return timeB - timeA;
  });
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    lab: LabRequest;
  }) => {
    setSelectedRequest(item);
    setLabComments(item.lab.labComments || ''); // Initialize labComments from existing labComments
    const initialResults: Record<string, string> = {
      ...(item.lab.results || {})
    };
    const hasFBC = item.lab.tests.includes('Full Blood Count') || item.lab.tests.includes('FBC');
    const hasUrinalysis = item.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = item.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = item.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = item.lab.tests.some(t => isAMHTest(t));
    // Only initialize empty strings for keys that don't already have a saved value
    const ensureKey = (k: string) => {
      if (initialResults[k] === undefined) initialResults[k] = '';
    };
    item.lab.tests.forEach(test => {
      if ((test === 'Full Blood Count' || test === 'FBC') && hasFBC) {
        fbcStructure.forEach(cat => {
          cat.tests.forEach(t => ensureKey(t.name));
        });
      } else if (test.toLowerCase().includes('urinalysis') && hasUrinalysis) {
        urinalysisTests.forEach(t => ensureKey(`UA_${t.name}`));
      } else if (test.toLowerCase().includes('testosterone') && hasTestosterone) {
        testosteroneTests.forEach(t => ensureKey(`TESTO_${t.name}`));
      } else if (test.toLowerCase().includes('hormonal profile') && hasHormonalProfile) {
        hormonalProfileTests.forEach(h => ensureKey(`HP_${h.key}`));
      } else if (isAMHTest(test) && hasAMH) {
        ensureKey(amhStructure.storageKey);
      } else if (isHCGTest(test)) {
        ensureKey(hcgStructure.storageKey);
      } else {
        ensureKey(test);
      }
    });
    if (item.lab.otherTests) ensureKey('Others');
    setResults(initialResults);
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const {
      enc,
      lab
    } = selectedRequest;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Completed',
      results,
      labComments,
      performedBy: currentUser.fullName,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    // Check if all labs are completed
    const allLabsCompleted = updatedLabRequests.every(l => l.status === 'Completed');
    const updateData: Partial<Encounter> = {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompleted
    };
    // If all labs done and no pending radiology, move back to clinician
    if (allLabsCompleted && !enc.pendingRadiology) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  const handleCancelConfirm = () => {
    if (!cancelModal.item || !currentUser || !cancelModal.reason.trim()) return;
    const {
      enc,
      lab
    } = cancelModal.item;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Cancelled',
      cancelledAt: new Date().toISOString(),
      cancelledBy: currentUser.id,
      cancellationReason: cancelModal.reason
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    const allLabsCompletedOrCancelled = updatedLabRequests.every(l => l.status === 'Completed' || l.status === 'Cancelled');
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompletedOrCancelled
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.cancel',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: cancelModal.reason,
      labRequestId: lab.id
    });
    setCancelModal({
      isOpen: false,
      item: null,
      reason: ''
    });
  };
  const handleRescheduleConfirm = () => {
    if (!rescheduleModal.item || !currentUser || !rescheduleModal.reason.trim() || !rescheduleModal.newDate) return;
    const {
      enc,
      lab
    } = rescheduleModal.item;
    const now = new Date().toISOString();
    const newHistoryEntry = {
      from: lab.rescheduledFor || lab.requestedAt,
      to: rescheduleModal.newDate,
      at: now,
      by: currentUser.id,
      reason: rescheduleModal.reason
    };
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Rescheduled',
      rescheduledFor: rescheduleModal.newDate,
      rescheduledAt: now,
      rescheduledBy: currentUser.id,
      rescheduleReason: rescheduleModal.reason,
      rescheduleHistory: [...(lab.rescheduleHistory || []), newHistoryEntry]
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.reschedule',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: rescheduleModal.reason,
      labRequestId: lab.id,
      previousDate: lab.rescheduledFor || lab.requestedAt,
      newDate: rescheduleModal.newDate
    });
    setRescheduleModal({
      isOpen: false,
      item: null,
      newDate: '',
      reason: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-2734">Patient not found</div>;
    const hasFBC = selectedRequest.lab.tests.includes('Full Blood Count') || selectedRequest.lab.tests.includes('FBC');
    const hasUrinalysis = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = selectedRequest.lab.tests.some(t => isAMHTest(t));
    const hasHCG = selectedRequest.lab.tests.some(t => isHCGTest(t));
    const otherTests = selectedRequest.lab.tests.filter(t => t !== 'Full Blood Count' && t !== 'FBC' && !t.toLowerCase().includes('urinalysis') && !t.toLowerCase().includes('testosterone') && !t.toLowerCase().includes('hormonal profile') && !isAMHTest(t) && !isHCGTest(t));
    return <div className="space-y-6 pb-20" data-id="element-2735">
        <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-2736">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2737">
            Enter Laboratory Results
          </h1>
          <div className="flex space-x-2" data-id="element-2738">
            <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-2739">
              Back to Queue
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-2740">
              <Printer className="w-4 h-4 mr-2" data-id="element-2741" /> Print Results
            </Button>
            <Button onClick={handleSaveResults} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2742">
              <CheckCircle className="w-5 h-5 mr-2" data-id="element-2743" /> Save & Send to Clinician
            </Button>
          </div>
        </div>

        {/* Printable Area */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 print:border-none print:shadow-none print:p-0" data-id="element-2744">
          {/* Print Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-2745">
            <div className="flex items-center" data-id="element-2746">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-2747" />
              <div data-id="element-2748">
                <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2749">
                  BMED MEDICAL CENTRE
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-2750">
                  Caring Healing Leading
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-2751">
              <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2752">
                LABORATORY REPORT
              </p>
              <p className="text-sm" data-id="element-2753">
                <span className="font-semibold" data-id="element-2754">Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-2755">
                <span className="font-semibold" data-id="element-2756">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-2757">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2758">
              <div className="col-span-8" data-id="element-2759">
                <span className="font-semibold" data-id="element-2760">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-2761">
                <span className="font-semibold" data-id="element-2762">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-2763">
                <span className="font-semibold" data-id="element-2764">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-2765">
              <div className="col-span-6" data-id="element-2766">
                <span className="font-semibold" data-id="element-2767">Requested By:</span>{' '}
                {selectedRequest.enc.lastAttendingClinician}
              </div>
              <div className="col-span-6" data-id="element-2768">
                <span className="font-semibold" data-id="element-2769">Requested On:</span>{' '}
                {new Date(selectedRequest.lab.requestedAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-8" data-id="element-2770">
            {hasFBC && <div data-id="element-2771">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2772">
                  Full Blood Count + Differential (FBC + DC)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2773">
                  <thead data-id="element-2774">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2775">
                      <th className="p-2 text-left font-semibold" data-id="element-2776">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-2777">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2778">
                        Reference range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2779">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2780">
                    {fbcStructure.map(category => <Fragment key={category.category} data-id="element-2781">
                        <tr data-id="element-2782">
                          <td colSpan={4} className="p-2 font-bold bg-gray-50 uppercase pt-4" data-id="element-2783">
                            {category.category}
                          </td>
                        </tr>
                        {category.tests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-2784">
                            <td className="p-2 pl-4" data-id="element-2785">{test.name}</td>
                            <td className="p-2" data-id="element-2786">
                              <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test.name] || ''} onChange={e => setResults({
                        ...results,
                        [test.name]: e.target.value
                      })} placeholder="Enter value" data-id="element-2787" />
                            </td>
                            <td className="p-2 text-gray-600" data-id="element-2788">{test.range}</td>
                            <td className="p-2 text-gray-600" data-id="element-2789">{test.unit}</td>
                          </tr>)}
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasUrinalysis && <div data-id="element-2790">
                <h3 className="font-bold text-lg mb-3 uppercase text-center border-b-2 border-black pb-1" data-id="element-2791">
                  Urinalysis Report
                </h3>
                <div className="max-w-md mx-auto space-y-2 py-4" data-id="element-2792">
                  {urinalysisTests.map(test => <div key={test.name} className="flex items-center" data-id="element-2793">
                      <span className="w-16 font-bold text-sm" data-id="element-2794">
                        {test.name}
                      </span>
                      <span className="mx-3 text-gray-400" data-id="element-2795">-</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium text-sm" value={results[`UA_${test.name}`] || ''} onChange={e => setResults({
                  ...results,
                  [`UA_${test.name}`]: e.target.value
                })} placeholder="Enter result" data-id="element-2796" />
                    </div>)}
                </div>
                <div className="max-w-md mx-auto mt-8 grid grid-cols-2 gap-4 text-sm" data-id="element-2797">
                  <div data-id="element-2798">
                    <label className="block font-semibold mb-1" data-id="element-2799">
                      Dr.'s Name:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_NAME'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_NAME']: e.target.value
                })} data-id="element-2800" />
                  </div>
                  <div data-id="element-2801">
                    <label className="block font-semibold mb-1" data-id="element-2802">
                      Signature:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_SIG'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_SIG']: e.target.value
                })} placeholder="Initials / signature" data-id="element-2803" />
                  </div>
                </div>
              </div>}

            {hasTestosterone && <div data-id="element-2804">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2805">
                  Testosterone
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2806">
                  <thead data-id="element-2807">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2808">
                      <th className="p-2 text-left font-semibold" data-id="element-2809">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-2810">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2811">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2812">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2813">
                    {testosteroneTests.map(test => <Fragment key={test.name} data-id="element-2814">
                        <tr className="border-b border-gray-100" data-id="element-2815">
                          <td className="p-2 font-medium" data-id="element-2816">{test.name}</td>
                          <td className="p-2" data-id="element-2817">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`TESTO_${test.name}`] || ''} onChange={e => setResults({
                        ...results,
                        [`TESTO_${test.name}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-2818" />
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2819">
                            <div data-id="element-2820">Males: {test.rangeMale}</div>
                            <div data-id="element-2821">Females: {test.rangeFemale}</div>
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2822">{test.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2823">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2824">
                            Method: {test.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasHormonalProfile && <div data-id="element-2825">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2826">
                  Hormonal Profile
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2827">
                  <thead data-id="element-2828">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2829">
                      <th className="p-2 text-left font-semibold" data-id="element-2830">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2831">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2832">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2833">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2834">
                    {hormonalProfileTests.map(hormone => <Fragment key={hormone.key} data-id="element-2835">
                        <tr className="bg-gray-50" data-id="element-2836">
                          <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2837">
                            {hormone.name}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 align-top" data-id="element-2838">
                          <td className="p-2 font-medium" data-id="element-2839">{hormone.name}</td>
                          <td className="p-2" data-id="element-2840">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`HP_${hormone.key}`] || ''} onChange={e => setResults({
                        ...results,
                        [`HP_${hormone.key}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-2841" />
                          </td>
                          <td className="p-2 text-gray-600 text-xs" data-id="element-2842">
                            {hormone.ranges.map(r => <div key={r.label} data-id="element-2843">
                                <span className="font-medium" data-id="element-2844">{r.label}:</span>{' '}
                                {r.value}
                              </div>)}
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2845">{hormone.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2846">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2847">
                            Method: {hormone.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasAMH && <div data-id="element-2848">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2849">
                  Anti Mullerian Hormone (AMH)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2850">
                  <thead data-id="element-2851">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2852">
                      <th className="p-2 text-left font-semibold" data-id="element-2853">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2854">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2855">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2856">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2857">
                    <tr className="bg-gray-50" data-id="element-2858">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2859">
                        Anti Mullerian Hormone (AMH)
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-2860">
                      <td className="p-2 font-medium" data-id="element-2861">{amhStructure.name}</td>
                      <td className="p-2" data-id="element-2862">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[amhStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [amhStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-2863" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-2864">
                        <div className="font-medium mb-1" data-id="element-2865">By age (ng/mL):</div>
                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2866">
                            <span className="font-medium" data-id="element-2867">{a.ageBand}:</span>{' '}
                            {a.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-2868">{amhStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2869">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2870">
                        Method: {amhStructure.method}
                      </td>
                    </tr>
                    <tr data-id="element-2871">
                      <td colSpan={4} className="p-2 text-xs text-gray-600 bg-amber-50 border-t border-amber-200" data-id="element-2872">
                        <p className="font-bold uppercase mb-1" data-id="element-2873">
                          Interpretation
                        </p>
                        <ul className="list-disc pl-5 space-y-0.5" data-id="element-2874">
                          {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2875">{line}</li>)}
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {hasHCG && <div data-id="element-2876">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2877">
                  Beta HCG
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2878">
                  <thead data-id="element-2879">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2880">
                      <th className="p-2 text-left font-semibold" data-id="element-2881">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2882">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-72" data-id="element-2883">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2884">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2885">
                    <tr className="bg-gray-50" data-id="element-2886">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2887">
                        Beta HCG
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-2888">
                      <td className="p-2 font-medium" data-id="element-2889">{hcgStructure.name}</td>
                      <td className="p-2" data-id="element-2890">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[hcgStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [hcgStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-2891" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-2892">
                        {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2893">
                            <span className="font-medium" data-id="element-2894">{r.label}:</span>{' '}
                            {r.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-2895">{hcgStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2896">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2897">
                        Method: {hcgStructure.method}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {(otherTests.length > 0 || selectedRequest.lab.otherTests) && <div data-id="element-2898">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2899">
                  Other Investigations
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2900">
                  <thead data-id="element-2901">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2902">
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-2903">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-2904">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2905">
                    {otherTests.map(test => <tr key={test} className="border-b border-gray-100" data-id="element-2906">
                        <td className="p-2 font-medium" data-id="element-2907">{test}</td>
                        <td className="p-2" data-id="element-2908">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test] || ''} onChange={e => setResults({
                      ...results,
                      [test]: e.target.value
                    })} placeholder="Enter result..." data-id="element-2909" />
                        </td>
                      </tr>)}
                    {selectedRequest.lab.otherTests && <tr className="border-b border-gray-100" data-id="element-2910">
                        <td className="p-2 font-medium" data-id="element-2911">
                          Others: {selectedRequest.lab.otherTests}
                        </td>
                        <td className="p-2" data-id="element-2912">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results['Others'] || ''} onChange={e => setResults({
                      ...results,
                      ['Others']: e.target.value
                    })} placeholder="Enter result..." data-id="element-2913" />
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Comments / Observations / Interpretation */}
          <div className="mt-8" data-id="element-2914">
            <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2915">
              Comments / Observations / Interpretation
            </h3>
            <textarea className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0" rows={4} placeholder="Enter any additional observations, interpretations, or comments..." value={labComments} onChange={e => setLabComments(e.target.value)} // Handle input for labComments
          data-id="element-2916" />
          </div>

          {/* Footer Signatures */}
          <div className="mt-16 pt-8 border-t border-gray-300" data-id="element-2917">
            <div className="flex justify-between items-end" data-id="element-2918">
              <div className="w-64" data-id="element-2919">
                <p className="text-sm font-semibold mb-8" data-id="element-2920">
                  Laboratory Technologist:
                </p>
                <div className="border-b border-black" data-id="element-2921"></div>
                <p className="text-sm mt-2 font-medium" data-id="element-2922">
                  {currentUser?.fullName}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2923">
                  BMED Laboratory Dept
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-2924">
      <div className="flex justify-between items-center" data-id="element-2925">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2926">
          Laboratory Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2927">
          <Badge variant="warning" className="text-sm px-3 py-1" data-id="element-2928">
            {pendingRequests.length} Pending
          </Badge>
          {rescheduledRequests.length > 0 && <Badge variant="info" className="text-sm px-3 py-1 bg-orange-100 text-orange-800" data-id="element-2929">
              {rescheduledRequests.length} Rescheduled
            </Badge>}
        </div>
      </div>

      <div className="space-y-6" data-id="element-2930">
        <Card title="Pending Requests" noPadding data-id="element-2931">
          <ul className="divide-y divide-gray-200" data-id="element-2932">
            {pendingRequests.length > 0 ? pendingRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-2933">
                    <div className="flex items-center" data-id="element-2934">
                      <div className="bg-yellow-100 p-2 rounded-full mr-4" data-id="element-2935">
                        <FlaskConical className="h-6 w-6 text-yellow-600" data-id="element-2936" />
                      </div>
                      <div data-id="element-2937">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2938">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2939">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-2940">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1" data-id="element-2941">
                          Requested:{' '}
                          {new Date(item.lab.requestedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-2942">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-2943">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-2944" /> Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-2945">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-2946" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2947">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2948">
                <FlaskConical className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2949" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2950">
                  No pending requests
                </p>
              </li>}
          </ul>
        </Card>

        {rescheduledRequests.length > 0 && <Card title="Rescheduled Requests" noPadding data-id="element-2951">
            <ul className="divide-y divide-gray-200" data-id="element-2952">
              {rescheduledRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 bg-orange-50/50 hover:bg-orange-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-l-4 border-orange-400" data-id="element-2953">
                    <div className="flex items-center" data-id="element-2954">
                      <div className="bg-orange-100 p-2 rounded-full mr-4" data-id="element-2955">
                        <CalendarClock className="h-6 w-6 text-orange-600" data-id="element-2956" />
                      </div>
                      <div data-id="element-2957">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2958">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2959">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-2960">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <div className="mt-1 flex items-center gap-2" data-id="element-2961">
                          <Badge variant="warning" className="bg-orange-100 text-orange-800" data-id="element-2962">
                            Rescheduled to:{' '}
                            {new Date(item.lab.rescheduledFor!).toLocaleString()}
                          </Badge>
                          <span className="text-xs text-gray-500 italic" data-id="element-2963">
                            Reason: {item.lab.rescheduleReason}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-2964">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-2965">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-2966" /> Reschedule
                        Again
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-2967">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-2968" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2969">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          })}
            </ul>
          </Card>}

        <Card data-id="element-2970">
          <div className="flex justify-between items-center cursor-pointer" onClick={() => setShowHistory(!showHistory)} data-id="element-2971">
            <div className="flex items-center gap-2" data-id="element-2972">
              <History className="w-5 h-5 text-gray-500" data-id="element-2973" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2974">
                Cancelled & Rescheduled History
              </h3>
            </div>
            <Button variant="outline" size="sm" data-id="element-2975">
              {showHistory ? 'Hide' : 'Show'}
            </Button>
          </div>

          {showHistory && <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-2976">
              {historyRequests.length > 0 ? <ul className="space-y-3" data-id="element-2977">
                  {historyRequests.map((item, idx) => {
              const patient = getPatient(item.enc.patientId);
              const isCancelled = item.lab.status === 'Cancelled';
              return <li key={`${item.lab.id}-${idx}`} className="text-sm bg-gray-50 p-3 rounded border border-gray-200" data-id="element-2978">
                        <div className="flex justify-between mb-1" data-id="element-2979">
                          <span className="font-medium" data-id="element-2980">
                            {patient?.firstName} {patient?.lastName} (
                            {patient?.bmcFileNumber})
                          </span>
                          <span className="text-gray-500 text-xs" data-id="element-2981">
                            {new Date(isCancelled ? item.lab.cancelledAt! : item.lab.rescheduledAt!).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1" data-id="element-2982">
                          Tests:{' '}
                          <span className={isCancelled ? 'line-through text-red-700 opacity-70' : ''} data-id="element-2983">
                            {item.lab.tests.join(', ')} {item.lab.otherTests}
                          </span>
                        </p>
                        <div className="flex items-center gap-2 mt-2" data-id="element-2984">
                          <Badge variant={isCancelled ? 'danger' : 'warning'} className={!isCancelled ? 'bg-orange-100 text-orange-800' : ''} data-id="element-2985">
                            {isCancelled ? 'Cancelled' : 'Rescheduled'}
                          </Badge>
                          <span className="text-gray-600 italic text-xs" data-id="element-2986">
                            Reason:{' '}
                            {isCancelled ? item.lab.cancellationReason : item.lab.rescheduleReason}
                          </span>
                        </div>
                      </li>;
            })}
                </ul> : <p className="text-gray-500 text-sm text-center py-4" data-id="element-2987">
                  No history available.
                </p>}
            </div>}
        </Card>
      </div>

      {/* Modals */}
      {cancelModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2988">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2989">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2990">
              <XCircle className="w-5 h-5 mr-2" data-id="element-2991" /> Cancel Laboratory Request
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-2992">
              Are you sure you want to cancel this request? This action will be
              logged in the audit trail.
            </p>
            <div className="mb-4" data-id="element-2993">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2994">
                Reason for cancellation (required)
              </label>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={cancelModal.reason} onChange={e => setCancelModal({
            ...cancelModal,
            reason: e.target.value
          })} placeholder="e.g., Clinician requested by mistake, patient refused..." data-id="element-2995" />
            </div>
            <div className="flex justify-end space-x-3" data-id="element-2996">
              <Button variant="outline" onClick={() => setCancelModal({
            isOpen: false,
            item: null,
            reason: ''
          })} data-id="element-2997">
                Go Back
              </Button>
              <Button variant="danger" onClick={handleCancelConfirm} disabled={!cancelModal.reason.trim()} data-id="element-2998">
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>}

      {rescheduleModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2999">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-3000">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-orange-600" data-id="element-3001">
              <CalendarClock className="w-5 h-5 mr-2" data-id="element-3002" /> Reschedule Laboratory
              Request
            </h3>
            <div className="space-y-4" data-id="element-3003">
              <div data-id="element-3004">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3005">
                  New scheduled date & time
                </label>
                <input type="datetime-local" className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" value={rescheduleModal.newDate} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              newDate: e.target.value
            })} data-id="element-3006" />
              </div>
              <div data-id="element-3007">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3008">
                  Reason for rescheduling (required)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" rows={3} value={rescheduleModal.reason} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              reason: e.target.value
            })} placeholder="e.g., Reagents out of stock, patient unavailable..." data-id="element-3009" />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-3010">
              <Button variant="outline" onClick={() => setRescheduleModal({
            isOpen: false,
            item: null,
            newDate: '',
            reason: ''
          })} data-id="element-3011">
                Go Back
              </Button>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleRescheduleConfirm} disabled={!rescheduleModal.reason.trim() || !rescheduleModal.newDate} data-id="element-3012">
                Confirm Reschedule
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};